import type { VehicleFE } from 'domain/vehicle';
import { getOldestDeviation } from 'helper/getOldestDeviation';
import { setSeverity } from 'helper/setSeverity';
import { flattenFeDeviations } from 'helper/vehicleV2';
import { mapValues, pipe, sortBy } from 'remeda';
import type { GenericRecord } from 'services/utils';

import type { DeviationWithVIN, PrioritezedItem } from './types';

const MRM_IN_PROCESS_STATES = ['EXECUTING_MRM', 'MRC', 'RELEASING_MRC'] as const;

/** Sort key for a deviation's `sentAt`; a missing timestamp sorts first (epoch 0), as before. */
const sentAtKey = (deviation?: DeviationWithVIN): number => deviation?.sentAt?.getTime() ?? 0;

export const prioritizeDeviations = (vehicles?: GenericRecord<VehicleFE>) => {
    if (!vehicles) {
        return;
    }
    return pipe(
        Object.values(vehicles),
        _ =>
            _.reduce(
                (pre: Record<string, DeviationWithVIN[]>, cur) => ({
                    ...pre,
                    [cur.vin]: flattenFeDeviations(cur).map(deviation => ({ ...deviation, vin: cur.vin })),
                }),
                {}
            ),
        mapValues(sortBy([sentAtKey, 'asc'])),
        (grouped): DeviationWithVIN[][] => Object.values(grouped),
        sortedDeviations =>
            sortedDeviations.flatMap(sortedDeviation => {
                const fatalDeviation = sortedDeviation.find(deviation => deviation.severity === 'FATAL');
                const errorDeviation = sortedDeviation.find(deviation => deviation.severity === 'ERROR');
                const warningDeviation = sortedDeviation.find(deviation => deviation.severity === 'WARNING');

                switch (true) {
                    case !!fatalDeviation:
                        return [fatalDeviation];
                    case !!errorDeviation:
                        return [errorDeviation];
                    case !!warningDeviation:
                        return [warningDeviation];
                    default:
                        return [];
                }
            }),
        (_: DeviationWithVIN[]) => ({
            fatals: sortBy(
                _.filter(deviation => deviation.severity === 'FATAL'),
                [sentAtKey, 'asc']
            ),
            errors: sortBy(
                _.filter(deviation => deviation.severity === 'ERROR'),
                [sentAtKey, 'asc']
            ),
            warnings: sortBy(
                _.filter(deviation => deviation.severity === 'WARNING'),
                [sentAtKey, 'asc']
            ),
            infos: sortBy(
                _.filter(deviation => deviation.severity === 'INFO'),
                [sentAtKey, 'asc']
            ),
        })
    );
};

export const getLastCharacters = (value: string, numberOfCharacters: number) => value.slice(-numberOfCharacters);

const mrmOrdering: PrioritezedItem['mrmState']['value'][] = [
    'EXECUTING_MRM',
    'MRC',
    'RELEASING_MRC',
    'MRM_FAULTED',
    'MRM_ERROR',
    'NONE',
];

export const sortByMRMState = (vehicles?: PrioritezedItem[], severity?: PrioritezedItem['severity']) => {
    if (!vehicles) {
        return [];
    }

    if (!severity) {
        return vehicles.sort(
            (crr, nxt) => mrmOrdering.indexOf(crr.mrmState.value) - mrmOrdering.indexOf(nxt.mrmState.value)
        );
    }

    return vehicles
        .filter(vehicle => vehicle.severity === severity)
        .sort((crr, nxt) => mrmOrdering.indexOf(crr.mrmState.value) - mrmOrdering.indexOf(nxt.mrmState.value));
};

const severityOrdering: PrioritezedItem['severity'][] = ['FATAL', 'ERROR', 'WARNING', 'INFO', 'UNKNOWN'];

export const sortBySeverity = (items: PrioritezedItem[]) =>
    items?.sort((crr, nxt) => severityOrdering.indexOf(crr.severity) - severityOrdering.indexOf(nxt.severity));

export const parseIntoPVLItems = (
    vehicles: VehicleFE[]
): { inMRM: PrioritezedItem[]; others: PrioritezedItem[] } | undefined => {
    if (!vehicles) {
        return {
            inMRM: [],
            others: [],
        };
    }

    const isInMRMProcess = (vehicle: PrioritezedItem) =>
        MRM_IN_PROCESS_STATES.some(state => state === vehicle.mrmState.value);

    const isNotInMRMProcess = (vehicle: PrioritezedItem) => !isInMRMProcess(vehicle);

    const parsedItems: PrioritezedItem[] = vehicles.map(vehicle => {
        const deviations = flattenFeDeviations(vehicle);
        return {
            vin: vehicle.vin,
            name: vehicle.name,
            licensePlate: vehicle.licensePlate,
            severity: setSeverity(deviations),
            mrmState: {
                value: vehicle.mrm.value?.value?.state ?? 'NONE',
                updatedAt: vehicle.mrm.value?.updatedAt ?? null,
            },
            relativeTime: getOldestDeviation(deviations),
            missionId: vehicle.activeMissionId,
            connectivityState: vehicle.connectivity.value?.value?.state,
        };
    });

    return pipe(Object.values(parsedItems), _ => ({
        inMRM: sortBySeverity(_.filter(vehicle => isInMRMProcess(vehicle))),
        others: sortBySeverity(
            _.filter(vehicle => (!vehicle.mrmState || isNotInMRMProcess(vehicle)) && vehicle.severity !== 'UNKNOWN')
        ),
    }));
};
